// TES-1093: Gemeinsame Bausteine der rollenspezifischen Dashboards.
// Geteilt zwischen Dashboard.tsx (QM/generisch), TesterDashboard und
// MangelbearbeiterDashboard — eine Quelle für Header-Anatomie, Quick-Actions,
// KPI-Zeile und Status-Kachel-Sektionen (keine Klone zwischen den Sichten).
import type { ReactNode } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { AlertTriangle, ChevronRight, LayoutDashboard, Play, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { KpiStatCard } from "@/components/ui/kpi-stat-card";
import { StatCard } from "@/components/dashboard/StatCard";
import { PageHeader } from "@/components/layout/PageHeader";
import type { RoleStatCardDef } from "@/components/dashboard/roleStatCards";

/** Primäre Quick-Actions (screen-04): „Mangel erfassen" + „Prüfung starten" — beide Safety-Yellow.
 *  TES-1733 (N3): „Mangel erfassen" hat EINE kanonische Variante über Dashboard/Liste/Dialog —
 *  die `action`-Variante des vereinheitlichten Mangel-Dialogs (TES-1722). */
export function DashboardQuickActions() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div className="hidden md:flex gap-3">
      <Button
        variant="action"
        onClick={() => navigate("/defects")}
        className="font-bold shadow-sm gap-2"
      >
        <AlertTriangle className="h-4 w-4" />
        <span className="text-label-caps">{t("defects.create")}</span>
      </Button>
      <Button
        onClick={() => navigate("/inspections")}
        className="bg-secondary text-secondary-foreground font-bold hover:bg-secondary/90 shadow-sm gap-2"
      >
        <Play className="h-4 w-4" />
        <span className="text-label-caps">{t("inspections.start")}</span>
      </Button>
    </div>
  );
}

/**
 * Dashboard-Header-Anatomie (TES-1660): rendert ueber den geteilten {@link PageHeader},
 * damit der Dashboard-Titel an der EXAKT gleichen vertikalen Position + mit identischer
 * Typografie sitzt wie auf allen anderen Seiten (Defects/Inspections/Tasks/Checklists).
 *
 * Die "Arbeitsbereich > Name"-Breadcrumb bleibt als kompakte Eyebrow erhalten — sie wird
 * jetzt UNTER dem Titel (PageHeader-Subtitle-Slot) gefuehrt, sodass der Titel selbst die
 * oberste Zeile ist (gleicher top/left wie PageHeader). Der Dashboard-Akzent (`text-primary`)
 * bleibt ueber `titleClassName` erhalten.
 */
export function RoleDashboardHeader({
  userName,
  title,
  subtitle,
  actions,
}: {
  userName: string;
  title: string;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  const { t } = useTranslation();

  const breadcrumb = (
    <span className="flex items-center gap-2 text-muted-foreground text-label-caps">
      <span>{t("dashboard.bento.breadcrumbWorkspace")}</span>
      <ChevronRight className="h-3 w-3" />
      <span className="text-primary font-bold">{userName}</span>
    </span>
  );

  return (
    <PageHeader
      icon={LayoutDashboard}
      title={title}
      titleClassName="text-primary"
      subtitle={
        <span className="flex flex-col gap-1">
          {breadcrumb}
          {subtitle && <span className="text-sm">{subtitle}</span>}
        </span>
      }
      actions={actions}
    />
  );
}

export interface RoleKpiItem {
  testId: string;
  label: string;
  value: number;
  icon: LucideIcon;
  tone: "primary" | "secondary" | "error";
}

/** Persönliche KPI-Zeile (KpiStatCards, container-query-responsiv 2→4 Spalten). */
export function RoleKpiRow({ items, testId }: { items: RoleKpiItem[]; testId: string }) {
  return (
    <div className="grid container-stats-4 gap-3 sm:gap-4" data-testid={testId}>
      {items.map(({ testId: itemTestId, label, value, icon: Icon, tone }) => (
        <KpiStatCard
          key={itemTestId}
          data-testid={itemTestId}
          label={label}
          value={value}
          tone={tone}
          icon={<Icon />}
        />
      ))}
    </div>
  );
}

/** Status-Kachel-Sektion (h2 + StatCard-Grid) — testIds identisch zu Dashboard.tsx. */
export function RoleTileSection({
  title,
  icon: Icon,
  iconClassName,
  tiles,
  dataTour,
}: {
  title: string;
  icon: LucideIcon;
  iconClassName: string;
  tiles: RoleStatCardDef[];
  dataTour: string;
}) {
  return (
    <div data-tour={dataTour} className="space-y-4">
      <h2 className="text-lg font-semibold flex items-center gap-2">
        <Icon className={iconClassName} />
        {title}
      </h2>
      <div className="grid container-stats-6 gap-3 sm:gap-4">
        {tiles.map((tile) => (
          <StatCard key={tile.testId} {...tile} />
        ))}
      </div>
    </div>
  );
}
